import { Chip } from '@emailforge/ui';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import { useCrossClientQuery } from '@/features/preview/previewApi';
import { useGetTemplateQuery, usePreviewTemplateMutation } from '@/features/templates/templatesApi';
import { t } from '@/lib/i18n';

type Tab = 'inbox' | 'cross-client';

export function PreviewScreen() {
  const { templateId = '' } = useParams();
  const { data } = useGetTemplateQuery(templateId, { skip: !templateId });
  const [preview, { isLoading }] = usePreviewTemplateMutation();
  const [html, setHtml] = useState<string>('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [tab, setTab] = useState<Tab>('inbox');

  const { data: crossClient } = useCrossClientQuery(templateId, {
    skip: tab !== 'cross-client' || !templateId,
  });

  useEffect(() => {
    if (!templateId || !data?.latestVersion) return;
    const variables = (data.latestVersion.variablesSchema?.sampleData as Record<string, unknown>) ?? {};
    preview({ templateId, variables })
      .unwrap()
      .then((r) => {
        setHtml(r.html);
        setWarnings(r.warnings);
      })
      .catch(() => setHtml(''));
  }, [templateId, data?.latestVersion?.id, data?.latestVersion, preview]);

  return (
    <>
      <TopBar
        crumbs={[t('nav.templates'), data?.name ?? '…', 'Preview']}
        right={
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {crossClient?.provider === 'stub' ? 'stub previews · wire LITMUS_API_KEY for real' : isLoading ? 'Rendering…' : 'Server-rendered'}
          </div>
        }
      />

      <div
        style={{
          borderBottom: '1px solid var(--line)',
          background: 'var(--paper)',
          padding: '0 24px',
          display: 'flex',
          gap: 20,
        }}
      >
        {(['inbox', 'cross-client'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '12px 0',
              fontSize: 13,
              fontWeight: tab === t ? 600 : 500,
              color: tab === t ? 'var(--ink)' : 'var(--ink-3)',
              border: 'none',
              background: 'transparent',
              borderBottom: `2px solid ${tab === t ? 'var(--flame)' : 'transparent'}`,
              cursor: 'pointer',
            }}
          >
            {t === 'inbox' ? 'Inbox preview' : 'Cross-client (9)'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, padding: 24, background: 'var(--paper-2)', overflowY: 'auto' }}>
        {tab === 'inbox' && (
          <>
            {warnings.length > 0 && (
              <div
                style={{
                  maxWidth: 720,
                  margin: '0 auto 16px',
                  padding: 10,
                  background: 'var(--warn-soft)',
                  border: '1px solid oklch(0.85 0.10 75)',
                  borderRadius: 'var(--r-3)',
                  fontSize: 12,
                  color: 'oklch(0.45 0.14 75)',
                }}
              >
                <Icon.beaker size={12} /> {warnings.length} warning{warnings.length > 1 ? 's' : ''}:
                <ul style={{ margin: '4px 0 0 20px' }}>
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mf-card" style={{ maxWidth: 720, margin: '0 auto', padding: 0, overflow: 'hidden' }}>
              <div
                style={{
                  background: 'var(--paper-2)',
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--line-soft)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 12,
                  color: 'var(--ink-3)',
                }}
              >
                <div className="mf-tl-dots">
                  <i />
                  <i />
                  <i />
                </div>
                <span className="mono" style={{ flex: 1, textAlign: 'center' }}>
                  {data?.slug ?? 'template'} · preview
                </span>
              </div>
              <iframe
                title="Email preview"
                srcDoc={html}
                style={{ width: '100%', height: 720, border: 'none', background: 'white' }}
              />
            </div>
          </>
        )}

        {tab === 'cross-client' && (
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {(crossClient?.previews ?? []).map((p) => (
                <div key={p.client} className="mf-card" style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      height: 240,
                      background:
                        p.imageUrl
                          ? `url(${p.imageUrl}) center/cover`
                          : 'linear-gradient(135deg, var(--paper-3), var(--paper-2))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--ink-3)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      letterSpacing: 0.06,
                      textTransform: 'uppercase',
                    }}
                  >
                    {!p.imageUrl && (p.status === 'ok' ? 'Rendered' : 'Rendered w/ warnings')}
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 550, flex: 1 }}>{p.label}</div>
                      <Chip variant={p.status === 'ok' ? 'live' : p.status === 'warning' ? 'draft' : 'err'}>
                        {p.status}
                      </Chip>
                    </div>
                    {p.warnings.length > 0 && (
                      <ul style={{ margin: '6px 0 0 14px', padding: 0, fontSize: 11, color: 'var(--ink-3)' }}>
                        {p.warnings.map((w) => (
                          <li key={w}>{w}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
              {(!crossClient || crossClient.previews.length === 0) && (
                <div
                  className="mf-card"
                  style={{
                    gridColumn: 'span 3',
                    padding: 40,
                    textAlign: 'center',
                    color: 'var(--ink-3)',
                    fontSize: 13,
                  }}
                >
                  Loading cross-client previews…
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
